import styled from 'styled-components';
import { COLORS, DEVICES, pickThemeFontStyles } from '@/theme';

export const StyledPanel = styled.section`
  background: ${COLORS.WHITE};
  border: 1px solid ${COLORS.BRIGHT_GRAY};
  border-radius: 0.75rem;
  padding: 1rem;
  min-width: 0;

  @media screen and ${DEVICES.LAPTOP_S} {
    padding: 1rem 1rem 0.75rem;
  }

  .ant-table-cell {
    min-width: 0 !important;
  }

  .ant-table-thead > tr > th:first-child,
  .ant-table-tbody > tr > td:first-child {
    padding-inline: 0.35rem !important;
    width: 2rem;
    max-width: 2rem;
  }
`;

export const StyledPanelTitle = styled.h3`
  margin: 0 0 0.75rem;
  ${pickThemeFontStyles('16', '22', 'SEMI_BOLD')};
  color: ${COLORS.BLACK};
`;

export const StyledPanelNote = styled.p`
  margin: 0.5rem 0 0;
  ${pickThemeFontStyles('12', '18', 'NORMAL')};
  color: ${COLORS.GRAY_PRIMARY};
`;

export const StyledRecordsPanel = styled(StyledPanel)`
  background: #fefdef;
  border-color: ${COLORS.YELLOW_PRIMARY};
`;

export const StyledRecordsMetrics = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 1.5rem 3rem;
`;

export const StyledMetricGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;

  @media screen and ${DEVICES.TABLET_S} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media screen and ${DEVICES.LAPTOP_S} {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

export const StyledMetricGrid4 = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;

  @media screen and ${DEVICES.TABLET_S} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media screen and (min-width: 1300px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

export const StyledMetricGrid3 = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;

  @media screen and ${DEVICES.TABLET_S} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media screen and (min-width: 1300px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

export const StyledMetricCard = styled.div`
  background: ${COLORS.BRIGHT_GRAY};
  border: none;
  border-radius: 0.5rem;
  padding: 0.65rem 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
`;

export const StyledMetricLabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
  min-width: 0;
`;

export const StyledMetricLabel = styled.div`
  ${pickThemeFontStyles('12', '16', 'NORMAL')};
  color: ${COLORS.GRAY_PRIMARY};
  min-width: 0;
`;

export const StyledMetricValue = styled.div`
  ${pickThemeFontStyles('18', '24', 'SEMI_BOLD')};
  color: ${COLORS.BLACK};
`;

export const StyledMetricContext = styled.div`
  ${pickThemeFontStyles('12', '16', 'NORMAL')};
  color: ${COLORS.GRAY_PRIMARY};
`;

export const StyledToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
`;

export const StyledToggleGroup = styled.div`
  display: inline-flex;
  gap: 0.5rem;
  flex-wrap: wrap;

  .ant-select .ant-select-arrow {
    margin-top: -1px !important;
  }
`;

export const StyledToggleButton = styled.button<{ $active?: boolean }>`
  border: 1px solid ${({ $active }) => ($active ? COLORS.YELLOW_PRIMARY : COLORS.BRIGHT_GRAY)};
  background: ${COLORS.WHITE};
  color: ${COLORS.BLACK};
  border-radius: 0.5rem;
  padding: 0.5rem 0.875rem;
  cursor: pointer;
  ${pickThemeFontStyles('14', '20', 'SEMI_NORMAL')};
  box-shadow: ${({ $active }) => ($active ? `0 0 0 1px ${COLORS.YELLOW_PRIMARY}` : 'none')};

  &:hover {
    border-color: ${COLORS.YELLOW_PRIMARY};
  }
`;

export const StyledChartBox = styled.div`
  width: 100%;
  height: 260px;
  min-width: 0;
`;

export const StyledDonutWrap = styled.div`
  position: relative;
  width: 100%;
  max-width: 300px;
  height: 300px;
  margin: 0 auto;
  overflow: visible;
`;

export const StyledDonutCenter = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  text-align: center;
  padding: 0 1.5rem;
  ${pickThemeFontStyles('14', '18', 'BOLD')};
  color: ${COLORS.BLACK};
`;

export const StyledTwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;

  @media screen and ${DEVICES.LAPTOP_S} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const StyledChartWithAside = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  min-width: 0;

  @media screen and ${DEVICES.LAPTOP_S} {
    grid-template-columns: minmax(0, 1fr) minmax(300px, 380px);
  }

  > ${StyledPanel} {
    display: flex;
    flex-direction: column;

    ${StyledChartBox} {
      flex: 1;
      min-height: 260px;
      height: auto;
    }
  }
`;

/** YAxis width must fit compact ticks like "20,00 млн"; keep a matching small right pad. */
export const MAIN_METRICS_CHART_MARGIN = { top: 8, right: 8, left: 0, bottom: 0 } as const;
export const MAIN_METRICS_Y_AXIS_WIDTH = 88;

export const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  border-radius: 0.5rem;
  overflow: hidden;

  th,
  td {
    text-align: left;
    padding: 0.65rem 0.75rem;
    ${pickThemeFontStyles('12', '18', 'NORMAL')};
    vertical-align: middle;
  }

  thead th {
    background: ${COLORS.DARK_BLUE};
    color: ${COLORS.WHITE};
    ${pickThemeFontStyles('12', '16', 'SEMI_NORMAL')};
    border: none;
  }

  tbody td {
    border-bottom: 1px solid ${COLORS.BRIGHT_GRAY};
    color: ${COLORS.BLACK};
    background: ${COLORS.WHITE};
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
`;

export const StyledDot = styled.span<{ $color: string }>`
  width: 0.6rem;
  height: 0.6rem;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

export const StyledLegend = styled.ul`
  list-style: none;
  margin: 0.75rem 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

export const StyledLegendItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  ${pickThemeFontStyles('12', '18', 'NORMAL')};
`;

export const StyledSubtitle = styled.p`
  margin: 0.25rem 0 0;
  ${pickThemeFontStyles('14', '20', 'NORMAL')};
  color: ${COLORS.GRAY_PRIMARY};
`;

export const StyledTablePager = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.75rem;
`;

export const StyledPagerButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: 1px solid ${COLORS.BRIGHT_GRAY};
  border-radius: 0.5rem;
  background: ${COLORS.WHITE};
  color: ${COLORS.BLACK};
  cursor: pointer;
  line-height: 1;
  flex-shrink: 0;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &:not(:disabled):hover {
    border-color: ${COLORS.GRAY_PRIMARY};
  }
`;

export const StyledPagerLabel = styled.span`
  ${pickThemeFontStyles('12', '18', 'NORMAL')};
  color: ${COLORS.GRAY_PRIMARY};
  min-width: 3.5rem;
  text-align: center;
  white-space: nowrap;
  line-height: 2rem;
`;
